import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  HStack,
  KdsModal,
  KdsText,
  VStack,
} from "@kazaar-platform/design-system";
import { RiArrowRightLine, RiBox3Line } from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslate } from "@tolgee/react";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ControlledCombobox, ControlledTextField } from "@/components/Form";
import { type TechnicalOptionDto } from "@/config/apiClient";
import { OrderStatusEnum } from "@/config/apiClient.types";
import { FieldValueSource, useAddress, useExpertMode } from "@/hooks";
import { useGetAvailableOrdersOptions } from "@/hooks/orders/useGetAvailableOrdersOptions/useGetAvailableOrdersOptions";
import { type SendBriefModalProps } from "@/modals/Modal.types";
import { buildSandboxItemVersionsWithAddress } from "@/pages/CatalogMediumPage/CatalogMediumForm/CatalogMediumForm.schema";
import { allowBriefSubmitNavigationAtom, isBriefWithAIAtom } from "@/store";
import { NEW_ORDER_KEY } from "./ModalAddBriefToOrder.constants";
import {
  type InferredModalAddBriefToOrderSchema,
  modalAddBriefToOrderFormSchema,
} from "./ModalAddBriefToOrder.schema";

export const ModalAddBriefToOrder = ({
  values,
  isOpen = true,
  onOpenChange,
  initialAIValues,
}: SendBriefModalProps) => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const setAllowSubmitNavigation = useSetAtom(allowBriefSubmitNavigationAtom);
  const isBriefWithAI = useAtomValue(isBriefWithAIAtom);

  const methods = useForm<InferredModalAddBriefToOrderSchema>({
    resolver: zodResolver(modalAddBriefToOrderFormSchema),
    defaultValues: {
      productName: "",
      orderId: NEW_ORDER_KEY,
    },
  });
  const { useGetAddress, useGetDefaultAddress } = useAddress();
  const { data: addressesData } = useGetAddress();
  const { data: defaultAddressData } = useGetDefaultAddress();

  const { useSendBrief } = useExpertMode();
  const { mutate, data, isPending } = useSendBrief();

  const { availableOrders, options } = useGetAvailableOrdersOptions();

  const defaultAddress = addressesData?.data?.find(
    (a) => a.id === defaultAddressData?.data?.addressId
  );

  const handleSubmit = async () => {
    const orderId = methods.watch("orderId");

    const sandboxItemVersion = defaultAddress
      ? buildSandboxItemVersionsWithAddress({
          versionCount: 1,
          quantity: 1,
          address: defaultAddress,
        })
      : undefined;

    mutate({
      submitBriefDto: {
        isAiAssisted: !!isBriefWithAI,
        orderId: orderId === NEW_ORDER_KEY ? undefined : orderId,
        sandboxItem: {
          sandboxItemVersion,
          orderName:
            orderId === NEW_ORDER_KEY ? methods.watch("orderName") : undefined,
          name: methods.watch("productName"),
          deliverableAdmaId: values.deliverableId
            ? Number(values.deliverableId)
            : undefined,
          productAdmaId: values.productId
            ? Number(values.productId)
            : undefined,
          specificRequest: values.initialExpression,
          totalQuantity: Number(values.quantity || 1),
          numberOfVersion: Number(values.versionCount || 1),
          technicalOptions: values.fields
            ? Object.keys(values.fields)
                .filter(
                  (e) =>
                    values?.fields?.[Number(e)] &&
                    values.fields[Number(e)].value
                )
                .map((attrId, index) => {
                  if (!values?.fields) {
                    return undefined;
                  }

                  const field = values.fields[Number(attrId)];

                  let source: TechnicalOptionDto["source"] =
                    FieldValueSource.ASSISTANT;

                  if (
                    initialAIValues?.fields &&
                    initialAIValues?.fields[Number(attrId)]?.value !==
                      values.fields[Number(attrId)].value
                  ) {
                    source = FieldValueSource.USER;
                  }

                  return {
                    fieldId: values.fields[Number(attrId)].id,
                    name: values.fields[Number(attrId)].label,
                    position: index,
                    type: values.fields[Number(attrId)].type,
                    fieldValue:
                      field?.type !== "Dropdown list"
                        ? values.fields[Number(attrId)].value
                        : "",
                    ...(field?.type === "Dropdown list" && {
                      fieldValueId: Number(values.fields[Number(attrId)].value),
                    }),
                    source,
                  };
                })
                .filter((e) => e !== undefined)
            : undefined,
        },
      },
    });
  };

  useEffect(() => {
    const orderId = data?.data.orderId;
    const orderItemId = data?.data.orderItemId;

    if (!orderId) {
      return;
    }

    queryClient.invalidateQueries({
      queryKey: ["order"],
    });

    queryClient.invalidateQueries({
      queryKey: ["orders"],
    });

    setAllowSubmitNavigation(true);

    queueMicrotask(() => {
      const selectedOrderId = methods.getValues("orderId");
      const isNewOrder = selectedOrderId === NEW_ORDER_KEY;
      const selectedOrder = availableOrders?.find(
        (order) => order.id === orderId
      );

      const isInDraft =
        isNewOrder || selectedOrder?.status === OrderStatusEnum.DRAFT;

      if (isInDraft) {
        void navigate(`/orders/${orderId}?tabOrderItemId=${orderItemId}`);
      } else {
        void navigate(
          `/orders/${orderId}/order-items/${orderItemId}?tabOrderItemId=${orderItemId}`
        );
      }
    });
  }, [data, setAllowSubmitNavigation]);

  return (
    <KdsModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="sm"
      dialogProps={{
        dialogHeaderProps: {
          title: t("openBrief.modalAddBriefToOrder.title"),
          description: t("openBrief.modalAddBriefToOrder.description"),
        },
        content: (
          <FormProvider {...methods}>
            <form
              id="open-brief-form"
              onSubmit={methods.handleSubmit(handleSubmit)}
              noValidate
            >
              <VStack gap="sp-md">
                <ControlledTextField
                  name="productName"
                  label={t("openBrief.modalAddBriefToOrder.productNameLabel")}
                  placeholder={t(
                    "openBrief.modalAddBriefToOrder.productNamePlaceholder"
                  )}
                  iconLeft={RiBox3Line}
                />

                <HStack gap="sp-xs" pl="sp-xs">
                  <Box pt="sp-xl">
                    <RiArrowRightLine />
                  </Box>

                  <Box
                    bgColor="elevation.second"
                    p="sp-md"
                    rounded="br-sm"
                    w="100%"
                  >
                    <VStack gap="sp-xs">
                      <ControlledCombobox
                        name="orderId"
                        isDisabled={!availableOrders?.length}
                        placeholder={t("inputs.addProductToOrder.placeholder")}
                        options={options}
                        defaultValue={NEW_ORDER_KEY}
                      />

                      {methods.watch("orderId") === NEW_ORDER_KEY && (
                        <>
                          <ControlledTextField
                            maxLength={40}
                            dataCy="orderNameInput"
                            placeholder={t(
                              "order.mediumDetails.stepConfiguration.newOrderName"
                            )}
                            name="orderName"
                          />

                          <div className="ml-auto">
                            <KdsText size="caption-sm" color="text-bodyvariant">
                              {t("numberbOfWords", {
                                maxLimit: 40,
                                numberOfChar:
                                  methods.watch("orderName")?.length ?? 0,
                              })}
                            </KdsText>
                          </div>
                        </>
                      )}
                    </VStack>
                  </Box>
                </HStack>
              </VStack>
            </form>
          </FormProvider>
        ),
        dialogFooterProps: {
          buttonProps: [
            {
              children: t("buttons.cancel"),
              variant: "ghost",
              color: "neutral",
              isLoading: isPending,
              disabled: isPending,
              onPress: onOpenChange,
            },
            {
              disabled: isPending,
              isLoading: isPending,
              form: "open-brief-form",
              type: "submit",
              children: t("buttons.confirm"),
            },
          ],
        },
      }}
    />
  );
};
